import * as React from 'react';
import { useNavigate } from 'react-router';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  useNotificationFeed,
  useMarkSeen,
  useMarkRead,
  useMarkAllRead,
  clearDeliveredNotifications,
} from './use-notifications';
import { useNotificationUi } from './notification-ui-store';
import { routeFor } from './notification-routing';
import type { NotificationRow } from './api';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export function NotificationBell() {
  const open = useNotificationUi((s) => s.open);
  const setOpen = useNotificationUi((s) => s.setOpen);
  const navigate = useNavigate();
  const { data: feed } = useNotificationFeed();
  const markSeen = useMarkSeen();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const rows = feed?.data ?? [];
  const unseen = feed?.unseenCount ?? 0;
  const hasUnread = rows.some((n) => !n.readAt);

  // Drop this DEVICE's OS notifications while the dropdown is open. Kept out of
  // handleOpenChange and keyed on the newest row so a push ARRIVING while the
  // dropdown is open clears too — same rule as the Notifications page, so the
  // two surfaces can't drift. Unconditional, unlike markSeen: `unseen` is a
  // server-side per-user latch, already 0 once the feed has been read on any
  // other device or surface, while this device's tray still holds them.
  const newestId = rows[0]?.id;
  React.useEffect(() => {
    if (open) void clearDeliveredNotifications();
  }, [open, newestId]);

  // Opening the feed clears the badge (marks everything seen); rows stay bold
  // until each is opened individually. Standard bell behaviour.
  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next && unseen > 0) markSeen.mutate();
  }

  function onItemClick(n: NotificationRow) {
    if (!n.readAt) markRead.mutate(n.id);
    setOpen(false);
    // cancelled/removed route to the list; everything else to the job detail.
    navigate(routeFor(n.kind, n.data.jobId));
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
          <Bell className="h-4 w-4" />
          {unseen > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] leading-4 text-center">
              {unseen > 9 ? '9+' : unseen}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[calc(100vw-1rem)] max-w-96 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-medium text-sm">Notifications</span>
          {hasUnread && (
            <button className="text-xs text-muted-foreground hover:underline" onClick={() => markAllRead.mutate()}>
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {!rows.length && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">No notifications</div>
          )}
          {rows.map((n) => (
            <button
              key={n.id}
              onClick={() => onItemClick(n)}
              className={`w-full text-left px-3 py-2 border-b last:border-0 hover:bg-accent flex gap-2 cursor-pointer ${n.readAt ? '' : 'bg-accent/40'}`}
            >
              {!n.readAt && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{n.title}</span>
                <span className="block text-xs text-muted-foreground break-words">{n.body}</span>
                <span className="block text-[10px] text-muted-foreground mt-0.5">{relativeTime(n.createdAt)}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
